import React, { useEffect, useState } from "react";
import { useOutletContext, Navigate } from "react-router-dom";
import { api, RSD, fmtDateTime } from "../../lib/api";
import { fmtDuration, fmtTime } from "../../hooks/useShift";
import { ShiftMoneyCards, ShiftCoinsCard, ShiftSourceBreakdown, CashReconciliation, showCash } from "../../components/shift/ShiftCards";
import { OrderItemsDetail } from "../../components/OrderItemsDetail";
import { ChevronLeft, CircleSlash } from "lucide-react";

const dayLabel = (iso) => fmtDateTime(iso, "sr", false).replace(/\.+$/, ".");

function HistoryRow({ s, onOpen, paymentMethod }) {
  return (
    <button data-testid={`shift-row-${s.id}`} onClick={() => onOpen(s.id)}
      className="w-full text-left bg-white rounded-2xl border p-3 hover:border-brand-red/40 transition-colors">
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="font-display font-black">{dayLabel(s.started_at)}</p>
          <p className="text-xs text-slate-400">
            {fmtTime(s.started_at)} – {s.ended_at ? fmtTime(s.ended_at) : "u toku"} · {fmtDuration(s.started_at, s.ended_at)}
          </p>
        </div>
        {s.status === "OPEN"
          ? <span className="text-[10px] font-black uppercase bg-brand-green/10 text-brand-green rounded-full px-2 py-1">U toku</span>
          : <span className="text-[10px] font-black uppercase bg-slate-100 text-slate-500 rounded-full px-2 py-1">Zatvorena</span>}
      </div>
      <div className="grid grid-cols-3 gap-2 mt-2 text-sm">
        {showCash(paymentMethod) && (
          <div><p className="text-[10px] font-black uppercase text-slate-400">Gotovina</p>
            <p className="font-bold text-brand-green">{RSD(s.cash_sales)}</p></div>
        )}
        {paymentMethod !== "CASH" && (
          <div><p className="text-[10px] font-black uppercase text-slate-400">Kartica</p>
            <p className="font-bold text-blue-700">{RSD(s.card_sales)}</p></div>
        )}
        <div><p className="text-[10px] font-black uppercase text-slate-400">Ukupno</p>
          <p className="font-black">{RSD(s.total_sales)}</p></div>
      </div>
      <p className="text-xs text-slate-400 mt-1.5">
        {s.orders} porudžbina · Pokrenuo: {s.started_by_name || "—"}
        {s.ended_by_name ? ` · Zatvorio: ${s.ended_by_name}` : ""}
        {s.cash_difference !== null && s.cash_difference !== undefined
          ? ` · Razlika kase: ${s.cash_difference > 0 ? "+" : ""}${RSD(s.cash_difference)}` : ""}
      </p>
    </button>
  );
}

function ShiftDetail({ rid, sid, onBack, paymentMethod, onDenied }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/restaurant/${rid}/shifts/${sid}`).then(({ data }) => setData(data)).catch((e) => {
      setData(null);
      if (e?.response?.status === 403) onDenied?.();
    });
  }, [rid, sid, onDenied]);
  if (!data) return <p className="text-slate-400 text-sm">Učitavanje…</p>;
  const s = data.shift;
  return (
    <div data-testid="shift-detail" className="space-y-4">
      <button data-testid="shift-detail-back" onClick={onBack} className="flex items-center gap-1 text-sm font-bold text-brand-red">
        <ChevronLeft size={16} />Sve smene
      </button>
      <div className="bg-white rounded-2xl border p-3 grid grid-cols-3 gap-2 text-center">
        {[["Početak", fmtTime(s.started_at)], ["Kraj", s.ended_at ? fmtTime(s.ended_at) : "u toku"],
          ["Trajanje", fmtDuration(s.started_at, s.ended_at)]].map(([l, v]) => (
          <div key={l}>
            <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">{l}</p>
            <p className="font-display font-black text-lg">{v}</p>
          </div>
        ))}
      </div>
      <p className="text-sm text-slate-500">
        Pokrenuo: <b>{s.started_by_name || "—"}</b>{s.ended_by_name ? <> · Zatvorio: <b>{s.ended_by_name}</b></> : null}
      </p>
      <ShiftMoneyCards shift={s} paymentMethod={paymentMethod} big />
      <ShiftCoinsCard shift={s} />
      <ShiftSourceBreakdown shift={s} />
      {showCash(paymentMethod) && (
        <div className="space-y-2">
          <p className="text-xs font-black uppercase tracking-wider text-slate-400">Kasa (gotovina)</p>
          <p className="text-sm text-slate-500">Početno stanje: <b>{RSD(s.opening_cash_amount)}</b></p>
          <CashReconciliation expected={s.expected_drawer} counted={s.closing_cash_counted} difference={s.cash_difference} />
        </div>
      )}
      <div>
        <p className="text-xs font-black uppercase tracking-wider text-slate-400 mb-1">
          Porudžbine u smeni ({data.orders.length})
        </p>
        <div className="bg-white rounded-2xl border divide-y">
          {data.orders.length === 0 && <p className="p-3 text-sm text-slate-400">Nema porudžbina.</p>}
          {data.orders.map((o) => (
            <div key={o.id} className="p-3" data-testid={`shift-order-${o.id}`}>
              <div className="flex items-center justify-between gap-2 text-sm">
                <span className="font-black">ŠIFRA {o.public_code}</span>
                <span className="text-slate-400 text-xs">
                  {o.order_source === "RESTAURANT_INTERNAL" ? "Vista" : "Ponesi.rs"} · {(o.payment_method || "").toUpperCase()} · {o.status === "completed" ? "Završena" : o.status}
                </span>
                <span className="font-bold">{RSD(o.total)}</span>
              </div>
              <OrderItemsDetail items={o.items} testid={`shift-items-${o.id}`} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function UnassignedSales({ rid, paymentMethod }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/restaurant/${rid}/shifts/unassigned?days=30`).then(({ data }) => setData(data)).catch(() => setData(null));
  }, [rid]);
  if (!data || data.orders === 0) return null;
  return (
    <div data-testid="shift-unassigned" className="bg-white rounded-2xl border-2 border-brand-orange/40 p-3">
      <p className="flex items-center gap-1.5 text-[11px] font-black uppercase tracking-wider text-brand-orange">
        <CircleSlash size={13} />Prodaja van smene (30 dana)
      </p>
      <p className="text-sm text-slate-500 mt-1">
        {data.orders} završenih porudžbina je preuzeto dok nijedna smena nije bila aktivna. Ostaju trajno van smena,
        ali ulaze u ukupan promet restorana.
      </p>
      <div className="grid grid-cols-3 gap-2 mt-2 text-sm">
        {showCash(paymentMethod) && <div><p className="text-[10px] font-black uppercase text-slate-400">Gotovina</p><p data-testid="unassigned-cash" className="font-bold">{RSD(data.cash_sales)}</p></div>}
        {paymentMethod !== "CASH" && <div><p className="text-[10px] font-black uppercase text-slate-400">Kartica</p><p data-testid="unassigned-card" className="font-bold">{RSD(data.card_sales)}</p></div>}
        <div><p className="text-[10px] font-black uppercase text-slate-400">Ukupno</p><p data-testid="unassigned-total" className="font-black">{RSD(data.total_sales)}</p></div>
      </div>
    </div>
  );
}

export default function ShiftsPage() {
  const { restaurant, role, reload } = useOutletContext();
  const rid = restaurant.id;
  const paymentMethod = restaurant.payment_method || "BOTH";
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(null);
  // The shift archive is financial data: STAFF is redirected before anything is fetched or rendered.
  const canViewArchive = ["OWNER", "MANAGER", "PLATFORM_ADMIN"].includes((role || "").toUpperCase());

  useEffect(() => {
    if (!canViewArchive) return;
    api.get(`/restaurant/${rid}/shifts`).then(({ data }) => setRows(data)).catch((e) => {
      setRows([]);
      // Role was downgraded mid-session → drop the financial rows and re-read the canonical role.
      if (e?.response?.status === 403) { setOpen(null); reload?.(); }
    });
  }, [rid, open, canViewArchive, reload]);

  if (!canViewArchive) return <Navigate to={`/restaurant/${rid}/orders`} replace />;

  return (
    <div className="max-w-3xl" data-testid="shifts-page">
      <h1 className="font-display font-black text-2xl mb-4">Istorija smena</h1>
      {open ? (
        <ShiftDetail rid={rid} sid={open} paymentMethod={paymentMethod} onBack={() => setOpen(null)}
          onDenied={() => { setOpen(null); setRows([]); reload?.(); }} />
      ) : !rows ? <p className="text-slate-400 text-sm">Učitavanje…</p> : (
        <div className="space-y-3">
          <UnassignedSales rid={rid} paymentMethod={paymentMethod} />
          {rows.length === 0 && <p data-testid="shifts-empty" className="bg-white rounded-2xl border p-4 text-sm text-slate-400">Još nema zabeleženih smena.</p>}
          {rows.map((s) => <HistoryRow key={s.id} s={s} paymentMethod={paymentMethod} onOpen={setOpen} />)}
        </div>
      )}
    </div>
  );
}
